import z from 'zod';

export const TaskSubjectSchema = z.object({
  type: z.string(),
  id: z.string(),
});

export type TaskSubject = z.infer<typeof TaskSubjectSchema>;

/** The one runtime definition of a complete task instance. */
export const TaskInstanceSchema = z.object({
  id: z.string(),
  dbVersion: z.number().int().nonnegative(),
  catalogRevision: z.number().int().nonnegative(),
  taskKey: z.string(),
  surfaces: z.array(z.string()),
  subject: TaskSubjectSchema.optional(),
  priority: z.number(),
  createdAt: z.string(),
  modifiedAt: z.string(),
  header: z.string(),
  description: z.string().optional(),
  actionLabel: z.string(),
  completedHeader: z.string().optional(),
  completedDescription: z.string().optional(),
  dismissHeader: z.string().optional(),
  dismissDescription: z.string().optional(),
  dismissable: z.boolean(),
  status: z.enum(['NOT_STARTED', 'COMPLETED', 'DISMISSED']),
  locked: z.boolean(),
  lockedBy: z.array(z.string()),
});

export type TaskInstance = z.infer<typeof TaskInstanceSchema>;
export type TaskStatus = TaskInstance['status'];

export type TaskWriteResponse = {
  changed: TaskInstance[];
};

export type MaterializationSubject = TaskSubject;

export type MaterializationOutcome = 'CREATED' | 'EXISTING' | 'RETIRED' | 'REJECTED';

export type MaterializationResult = {
  subject: TaskSubject;
  outcome: MaterializationOutcome;
  instance?: TaskInstance;
  reason?: string;
};

export type TaskMaterializationResponse = {
  results: MaterializationResult[];
};

export type TaskResetResponse = {
  deletedCount: number;
};

export const TaskTombstoneSchema = z.object({
  id: z.string(),
  dbVersion: z.number().int().nonnegative(),
  isDeleted: z.literal(true),
});

export type TaskTombstone = z.infer<typeof TaskTombstoneSchema>;

const hasDeletedMarker = (value: unknown): value is { isDeleted: true } =>
  typeof value === 'object' && value !== null && 'isDeleted' in value && value.isDeleted === true;

export const parseTaskTombstone = (value: unknown): TaskTombstone | undefined => {
  const parsed = TaskTombstoneSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
};

/** Unknown or incomplete instances are not safe to merge or render. */
export const parseTaskInstance = (value: unknown): TaskInstance | undefined => {
  if (hasDeletedMarker(value)) return undefined;
  const parsed = TaskInstanceSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
};

/** Parses each resource independently so one future task shape cannot empty the collection. */
export const parseTaskInstances = (values: readonly unknown[]): TaskInstance[] =>
  values.flatMap((value) => {
    const task = parseTaskInstance(value);
    return task ? [task] : [];
  });
